import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
const COLORS = ['#000000', '#0071e3', '#c0392b', '#27ae60', '#e67e22'];
const THIN = 2;
const THICK = 6;
export const SketchCanvas = ({ onSave, existingDataUrl }) => {
    const isMobile = useIsMobile();
    const canvasRef = useRef(null);
    const isDrawingRef = useRef(false);
    const undoStackRef = useRef([]);
    const [tool, setTool] = useState('pen');
    const [color, setColor] = useState('#000000');
    const [strokeWidth, setStrokeWidth] = useState(THIN);
    const [saved, setSaved] = useState(false);
    const canvasHeight = isMobile ? 300 : 400;
    const getCtx = () => {
        const canvas = canvasRef.current;
        return canvas ? canvas.getContext('2d') : null;
    };
    const saveSnapshot = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = getCtx();
        if (!canvas || !ctx)
            return;
        const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
        undoStackRef.current = [...undoStackRef.current.slice(-19), snapshot];
    }, []);
    // Initialize canvas on mount
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Load existing sketch
        if (existingDataUrl) {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                saveSnapshot();
            };
            img.src = existingDataUrl;
        }
        else {
            saveSnapshot();
        }
        // Ensure touch scrolling is disabled on the canvas element
        canvas.style.touchAction = 'none';
    }, [existingDataUrl, saveSnapshot]);
    const getCanvasPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };
    const handlePointerDown = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        canvas.setPointerCapture(e.pointerId);
        isDrawingRef.current = true;
        saveSnapshot();
        const ctx = getCtx();
        const { x, y } = getCanvasPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        if (tool === 'eraser') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = '#ffffff';
        }
        else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = color;
        }
        ctx.lineWidth = strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    };
    const handlePointerMove = (e) => {
        e.preventDefault();
        if (!isDrawingRef.current)
            return;
        const ctx = getCtx();
        const { x, y } = getCanvasPos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };
    const handlePointerUp = (e) => {
        e.preventDefault();
        isDrawingRef.current = false;
        const ctx = getCtx();
        if (ctx)
            ctx.closePath();
    };
    const handleUndo = () => {
        const canvas = canvasRef.current;
        const ctx = getCtx();
        if (!canvas || !ctx)
            return;
        if (undoStackRef.current.length <= 1)
            return;
        const newStack = undoStackRef.current.slice(0, -1);
        undoStackRef.current = newStack;
        const prev = newStack[newStack.length - 1];
        ctx.putImageData(prev, 0, 0);
    };
    const handleClear = () => {
        const canvas = canvasRef.current;
        const ctx = getCtx();
        if (!canvas || !ctx)
            return;
        saveSnapshot();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        canvas.toBlob((blob) => {
            if (blob) {
                onSave(blob);
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
            }
        }, 'image/png');
    };
    const toolBtnStyle = (active) => ({
        padding: '8px 16px',
        backgroundColor: active ? '#0071e3' : 'rgba(255,255,255,0.7)',
        color: active ? '#fff' : '#3c3c43',
        border: '1px solid ' + (active ? '#0071e3' : 'rgba(0,0,0,0.12)'),
        borderRadius: '980px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        minHeight: '36px',
        transition: 'all 0.15s ease',
    });
    return (_jsxs("div", { children: [_jsxs("div", { style: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    alignItems: 'center',
                    marginBottom: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.8)',
                }, children: [_jsx("button", { style: toolBtnStyle(tool === 'pen'), onClick: () => setTool('pen'), children: "\u270F\uFE0F Pen" }), _jsx("button", { style: toolBtnStyle(tool === 'eraser'), onClick: () => setTool('eraser'), children: "\u2B1C Eraser" }), _jsx("div", { style: {
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        } }), COLORS.map((c) => (_jsx("button", { onClick: () => {
                            setColor(c);
                            setTool('pen');
                        }, style: {
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: c,
                            border: color === c && tool === 'pen' ? '3px solid #0071e3' : '2px solid rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            padding: 0,
                            flexShrink: 0,
                        } }, c))), _jsx("div", { style: {
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        } }), _jsx("button", { style: toolBtnStyle(strokeWidth === THIN), onClick: () => setStrokeWidth(THIN), children: "Thin" }), _jsx("button", { style: toolBtnStyle(strokeWidth === THICK), onClick: () => setStrokeWidth(THICK), children: "Thick" }), _jsx("div", { style: {
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                        } }), _jsx("button", { onClick: handleUndo, style: {
                            padding: '8px 14px',
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            color: '#3c3c43',
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: '980px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            minHeight: '36px',
                        }, children: "\u21A9 Undo" }), _jsx("button", { onClick: handleClear, style: {
                            padding: '8px 14px',
                            backgroundColor: 'rgba(255,59,48,0.08)',
                            color: '#c0392b',
                            border: '1px solid rgba(255,59,48,0.2)',
                            borderRadius: '980px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            minHeight: '36px',
                        }, children: "Clear" })] }), _jsx("div", { style: {
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    cursor: tool === 'eraser' ? 'cell' : 'crosshair',
                }, children: _jsx("canvas", { ref: canvasRef, width: 800, height: canvasHeight, style: {
                        display: 'block',
                        width: '100%',
                        height: `${canvasHeight}px`,
                        touchAction: 'none',
                    }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp }) }), _jsxs("div", { style: { marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }, children: [_jsx("button", { onClick: handleSave, style: {
                            padding: '11px 24px',
                            backgroundColor: '#0071e3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '980px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            minHeight: '44px',
                        }, children: "Save Sketch" }), saved && (_jsx("span", { style: {
                            fontSize: '14px',
                            color: '#248a3d',
                            fontWeight: '500',
                            animation: 'fadeIn 0.2s ease',
                        }, children: "\u2713 Drawing saved" }))] })] }));
};
//# sourceMappingURL=SketchCanvas.js.map